/*
 * lander.js — interactive behaviour for the listicle lander sections.
 *
 * Loaded with `defer` from layout/theme.liquid for `template contains 'listicle'`.
 *
 * WHY AN ASSET AND NOT {% javascript %}: this theme never delivers the section
 * JavaScript bundle. {% stylesheet %} blocks are inlined into the page, but
 * {% javascript %} content appears nowhere in the rendered HTML — which is why
 * the pre-existing <listicle-sticky-cta> element has never been defined on the
 * live landers. Anything interactive here has to come from a real asset.
 *
 * Everything is a custom element, so a section that is not on the page simply
 * never upgrades, and re-rendering in the theme editor re-runs connectedCallback.
 */
(function () {
  'use strict';

  window.Roostys = window.Roostys || {};

  /* ---------------------------------------------------------------------
   * Countdown — owns the remaining time for the whole page, and fills every
   * .lander-cta__timer so the bar and the CTA sub-lines run off one timer.
   *
   * Two sources:
   *
   *   sitewide — the same expression the storefront header runs
   *              (sections/annoucement-bar-v2.liquid with auto countdown on):
   *              cycle - (Date.now() % cycle). Derived from the clock alone, so
   *              every visitor sees the same value at the same instant and a
   *              visitor moving from this lander to a PDP sees a continuous
   *              clock. Nothing is persisted.
   *
   *   visit    — a per-visitor window persisted in localStorage, which starts
   *              on first view and survives a reload.
   * ------------------------------------------------------------------- */
  class ListicleCountdown extends HTMLElement {
    connectedCallback() {
      this.source = this.dataset.source === 'visit' ? 'visit' : 'sitewide';
      this.storageKey = this.dataset.storageKey || 'rs-lander-deadline';
      this.hours = parseInt(this.dataset.hours, 10) || 24;

      var cycleDays = parseInt(this.dataset.cycleDays, 10);
      var cycleHours = parseInt(this.dataset.cycleHours, 10);
      if (isNaN(cycleDays)) cycleDays = 5;
      if (isNaN(cycleHours)) cycleHours = 4;
      this.cycleMs = (cycleDays * 24 + cycleHours) * 3600 * 1000;

      // Only the per-visitor source needs a stored deadline.
      this.deadline = this.source === 'visit' ? this._resolveDeadline() : null;

      this.parts = {
        days: this.querySelector('[data-countdown-days]'),
        hours: this.querySelector('[data-countdown-hours]'),
        minutes: this.querySelector('[data-countdown-minutes]'),
        seconds: this.querySelector('[data-countdown-seconds]'),
      };

      // CTA sub-lines render hidden so a page with no countdown degrades to its
      // static half. Reveal them now that a timer is actually running.
      document.querySelectorAll('.lander-cta__countdown').forEach(function (el) {
        el.removeAttribute('hidden');
      });

      this._tick();
      this.timer = setInterval(this._tick.bind(this), 1000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    _resolveDeadline() {
      var stored = null;
      try {
        stored = Number(localStorage.getItem(this.storageKey)) || null;
      } catch (e) {
        // Private mode or blocked storage: fall through to a per-visit deadline.
      }

      if (stored && stored > Date.now()) return stored;

      var fresh = Date.now() + this.hours * 3600 * 1000;
      try {
        localStorage.setItem(this.storageKey, String(fresh));
      } catch (e) {
        /* not fatal */
      }
      return fresh;
    }

    _pad(value) {
      return String(Math.max(0, Math.floor(value))).padStart(2, '0');
    }

    _remaining() {
      if (this.source === 'sitewide') {
        // Identical to the storefront header's auto countdown. Self-resetting
        // by construction, so it never needs an expiry branch.
        return this.cycleMs - (Date.now() % this.cycleMs);
      }

      var left = this.deadline - Date.now();
      if (left <= 0) {
        // Expired: start a fresh window rather than sitting on a dead clock.
        try {
          localStorage.removeItem(this.storageKey);
        } catch (e) {
          /* not fatal */
        }
        this.deadline = this._resolveDeadline();
        left = this.deadline - Date.now();
      }
      return left;
    }

    _tick() {
      var left = this._remaining();

      // The site-wide cycle runs to just over five days, so total hours would
      // read "124h" in a three-pill bar. Days are carried into their own pill
      // only while there are any, and the hours pill stays under 24.
      var days = Math.floor(left / 86400000);
      var h = this._pad((left / 3600000) % 24);
      var m = this._pad((left / 60000) % 60);
      var s = this._pad((left / 1000) % 60);
      var d = days > 0 ? this._pad(days) + 'd ' : '';

      if (this.parts.days) {
        this.parts.days.textContent = this._pad(days) + 'd';
        this.parts.days.hidden = days === 0;
      }
      if (this.parts.hours) this.parts.hours.textContent = h + 'h';
      if (this.parts.minutes) this.parts.minutes.textContent = m + 'm';
      if (this.parts.seconds) this.parts.seconds.textContent = s + 's';

      var label = d + h + 'h ' + m + 'm ' + s + 's';
      document.querySelectorAll('.lander-cta__timer').forEach(function (el) {
        el.textContent = label;
      });
    }
  }

  /* ---------------------------------------------------------------------
   * Before / after — the range input drives a custom property that both the
   * clip-path and the divider read. No transition: it has to track the finger.
   * ------------------------------------------------------------------- */
  class ListicleBeforeAfter extends HTMLElement {
    connectedCallback() {
      this.range = this.querySelector('.lander-ba__range');
      if (!this.range) return;
      this._onInput = this._onInput.bind(this);
      this.range.addEventListener('input', this._onInput);
    }

    disconnectedCallback() {
      if (this.range) this.range.removeEventListener('input', this._onInput);
    }

    _onInput() {
      this.style.setProperty('--lander-ba-pos', this.range.value + '%');
    }
  }

  /* ---------------------------------------------------------------------
   * Testimonial carousel — index state driving a translateX on the track.
   * Wraps in both directions so neither control is ever a dead end.
   * ------------------------------------------------------------------- */
  class ListicleCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-carousel-track]');
      this.status = this.querySelector('[data-carousel-status]');
      this.count = parseInt(this.dataset.count, 10) || 1;
      this.index = 0;

      var prev = this.querySelector('[data-carousel-prev]');
      var next = this.querySelector('[data-carousel-next]');
      if (prev) prev.addEventListener('click', this._go.bind(this, -1));
      if (next) next.addEventListener('click', this._go.bind(this, 1));
    }

    _go(step) {
      this.index = (this.index + step + this.count) % this.count;
      if (this.track) {
        this.track.style.transform = 'translateX(-' + this.index * 100 + '%)';
      }
      if (this.status) {
        this.status.textContent = this.index + 1 + ' / ' + this.count;
      }
    }
  }

  /* ---------------------------------------------------------------------
   * Sticky bottom bar — slides up past a scroll threshold.
   *
   * Not dismissable: the close control was mobile-only and has been removed, so
   * there is no dismissed state to track either.
   *
   * The class itself is unchanged from the one that has always sat in the
   * section's {% javascript %} block. It lives here because that block is
   * never delivered: verified against production as well as the dev server,
   * where "ListicleStickyCta" appears nowhere in the HTML. The element has
   * therefore never upgraded, and the bar has never appeared on any of the
   * three landers despite being in all three templates.
   * ------------------------------------------------------------------- */
  class ListicleStickyCta extends HTMLElement {
    connectedCallback() {
      this.threshold = parseInt(this.dataset.threshold, 10) || 500;
      this._onScroll = this._onScroll.bind(this);
      window.addEventListener('scroll', this._onScroll, { passive: true });
      this._watchDestination();
      this._onScroll();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this._onScroll);
      if (this._observer) this._observer.disconnect();
      document.body.classList.remove('listicle-bar-active');
    }

    /* When the bar's button jumps to something on this page (#offer), the bar steps
     * aside while that destination is on screen: a "go to the buy box" bar sitting
     * over the buy box is noise, and on desktop the buy box painted over it anyway.
     *
     * The anchor is usually a zero-height marker in a section of its own, directly
     * above the section it points at, so an empty anchor section hands over to the
     * next section. A bar that links off-page has no destination and is unaffected. */
    _watchDestination() {
      this._atDestination = false;
      const link = this.querySelector('a[href^="#"]');
      if (!link || link.hash.length < 2 || !('IntersectionObserver' in window)) return;
      const anchor = document.getElementById(decodeURIComponent(link.hash.slice(1)));
      if (!anchor) return;
      let zone = anchor.closest('.shopify-section') || anchor;
      if (zone.offsetHeight < 8 && zone.nextElementSibling) zone = zone.nextElementSibling;

      this._observer = new IntersectionObserver((entries) => {
        this._atDestination = entries[0].isIntersecting;
        this._onScroll();
      });
      this._observer.observe(zone);
    }

    _onScroll() {
      if (window.scrollY > this.threshold && !this._atDestination) this._show();
      else this._hide();
    }

    _show() {
      this.classList.add('is-visible');
      document.body.classList.add('listicle-bar-active');
    }

    _hide() {
      this.classList.remove('is-visible');
      document.body.classList.remove('listicle-bar-active');
    }
  }

  if (!customElements.get('listicle-sticky-cta')) {
    customElements.define('listicle-sticky-cta', ListicleStickyCta);
  }

  if (!customElements.get('listicle-countdown')) {
    customElements.define('listicle-countdown', ListicleCountdown);
  }
  if (!customElements.get('listicle-before-after')) {
    customElements.define('listicle-before-after', ListicleBeforeAfter);
  }
  if (!customElements.get('listicle-carousel')) {
    customElements.define('listicle-carousel', ListicleCarousel);
  }
  /* ---------------------------------------------------------------------
   * In-page jumps — the mid-page and sticky CTAs link to an anchor (#offer)
   * that can sit several screens down. A plain anchor jump teleports there; the
   * browser's own smooth scroll takes as long as the distance is far. This
   * glides in a fixed time whatever the distance.
   *
   * The target is re-measured every frame because lazy images above it are
   * still loading as the page passes them, which moves it.
   * ------------------------------------------------------------------- */
  const JUMP_MS = 650;
  let jumpFrame = 0;

  const glideTo = (target) => {
    const startY = window.scrollY;
    const startTime = performance.now();
    const targetY = () => target.getBoundingClientRect().top + window.scrollY;
    const cancel = () => cancelAnimationFrame(jumpFrame);

    cancel();
    // A visitor who scrolls mid-glide keeps control.
    ['wheel', 'touchstart', 'keydown'].forEach((type) => {
      window.addEventListener(type, cancel, { once: true, passive: true });
    });

    const step = (now) => {
      const t = Math.min(1, (now - startTime) / JUMP_MS);
      const eased = 1 - Math.pow(1 - t, 3);
      window.scrollTo(0, startY + (targetY() - startY) * eased);
      if (t < 1) jumpFrame = requestAnimationFrame(step);
    };
    jumpFrame = requestAnimationFrame(step);
  };

  // Delegated, so it survives the theme editor re-rendering a section.
  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest('a[href^="#"]');
    if (!link || link.hash.length < 2) return;
    const target = document.getElementById(decodeURIComponent(link.hash.slice(1)));
    if (!target) return;
    // Reduced motion keeps the browser's instant jump.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    event.preventDefault();
    history.pushState(null, '', link.hash);
    glideTo(target);
  });
})();
